"use client";
import React, { useState, useMemo, useRef } from "react";
import {
  Calendar, Store, Globe, Package, IndianRupee, CheckCircle,
  XCircle, Clock, Printer, CreditCard, Banknote, AlertCircle,
  BarChart3, FileText, ChevronDown, ChevronUp,
  CheckCircle2, Clock3,
} from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt  = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const fmtK = (n) => {
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(1)}L`;
  if (n >= 1_000)    return `₹${(n / 1_000).toFixed(1)}k`;
  return `₹${Math.round(n)}`;
};
const today  = () => new Date().toISOString().slice(0, 10);
const COLORS = ["#0F766E","#3B82F6","#F59E0B","#7C3AED","#EC4899","#EF4444","#10B981"];

const COUNTER_METHODS = ["cash","cod","counter","pos","walk-in","walk in"];
const ONLINE_METHODS  = ["upi","net banking","netbanking","online","credit card","debit card",
                         "card","razorpay","paytm","phonepe","gpay","google pay","stripe","neft","imps"];

function classifyChannel(order) {
  const pm  = (order.paymentMethod || order.payment_method || "").toLowerCase();
  const otl = (order.orderTypeLabel || "").toLowerCase();
  const gw  = order.payment_gateway;
  if (otl === "bulk")   return "bulk";
  if (otl === "custom") return "custom";
  if (gw)                                         return "online";
  if (ONLINE_METHODS.some(m => pm.includes(m)))   return "online";
  if (COUNTER_METHODS.some(m => pm.includes(m)))  return "counter";
  if (otl === "manaprint" || otl === "standard")  return "counter";
  return "other";
}

const isCancelled = (s) => /cancel|reject/i.test(s || "");
const isRefunded  = (s) => /refund|return/i.test(s || "");
const isCompleted = (s) => /deliver|complet/i.test(s || "");
const isPending   = (s) => !isCancelled(s) && !isRefunded(s) && !isCompleted(s);

const PRINT_CSS = `
  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    body * { visibility: hidden; }
    #day-end-report, #day-end-report * { visibility: visible; }
    #day-end-report { position: absolute; left: 0; top: 0; width: 100%; }
    .no-print { display: none !important; }
    @page { margin: 14mm; }
  }
`;

// ─── KPI Box ──────────────────────────────────────────────────────────────────
function KpiBox({ label, value, sub, color = "#0F766E", bg = "#F0FDFA", icon: Icon }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
          <Icon className="w-3.5 h-3.5" style={{ color }} />
        </div>
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-tight">{label}</span>
      </div>
      <p className="text-xl font-extrabold text-gray-900 tracking-tight">{value}</p>
      {sub && <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{sub}</p>}
    </div>
  );
}

// ─── Channel Panel ────────────────────────────────────────────────────────────
function ChannelPanel({ title, icon: Icon, color, bg, orders, paymentBreakdown }) {
  const [expanded, setExpanded] = useState(false);
  const revenue   = orders.reduce((s, o) => s + (o.total || 0), 0);
  const collected = orders.filter(o => (o.payment_status||"").toLowerCase() === "paid")
                          .reduce((s, o) => s + (o.total || 0), 0);
  const cancelled = orders.filter(o => isCancelled(o.rawStatus)).length;
  const completed = orders.filter(o => isCompleted(o.rawStatus)).length;

  return (
    <div className="bg-white border-2 rounded-2xl overflow-hidden shadow-sm" style={{ borderColor: `${color}30` }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ background: `${color}08` }}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: bg }}>
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold text-gray-800 leading-tight truncate">{title}</p>
            <p className="text-[11px]" style={{ color }}>{orders.length} orders · {fmtK(revenue)}</p>
          </div>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="p-1.5 rounded-lg hover:bg-black/5 transition-colors flex-shrink-0 ml-2">
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-px bg-gray-100">
        {[
          { label:"Revenue",   val: fmtK(revenue),   clr: color },
          { label:"Collected", val: fmtK(collected), clr:"#15803D" },
          { label:"Completed", val: completed,        clr:"#15803D" },
          { label:"Cancelled", val: cancelled,        clr:"#991B1B" },
        ].map(item => (
          <div key={item.label} className="bg-white px-3 py-2.5">
            <p className="text-[10px] text-gray-400 font-semibold mb-0.5">{item.label}</p>
            <p className="text-sm font-bold" style={{ color: item.clr }}>{item.val}</p>
          </div>
        ))}
      </div>

      <div className="px-4 py-3 border-t border-gray-100">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Payment Methods</p>
        <div className="space-y-1.5">
          {paymentBreakdown.length === 0 ? (
            <p className="text-xs text-gray-400">No payments recorded</p>
          ) : paymentBreakdown.map(pb => (
            <div key={pb.method} className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
                <span className="text-xs text-gray-600 capitalize truncate">{pb.method}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[10px] text-gray-400">{pb.count} txns</span>
                <span className="text-xs font-bold text-gray-800">{fmtK(pb.amount)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {expanded && orders.length > 0 && (
        <div className="border-t border-gray-100 max-h-48 overflow-y-auto">
          {orders.map((o, i) => (
            <div key={o.id} className={`flex items-center justify-between px-4 py-2.5 text-xs border-b border-gray-50 last:border-0 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-semibold text-gray-700 flex-shrink-0">{o.orderNumber}</span>
                <span className="text-gray-400 truncate">{o.customer?.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <span className="font-bold" style={{ color: isCancelled(o.rawStatus) ? "#991B1B" : color }}>
                  {fmt(o.total)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Reconciliation Row ───────────────────────────────────────────────────────
function ReconciliationRow({ label, amount, count, bold, negative, total, indent }) {
  return (
    <div className={`flex items-center justify-between py-2 ${total ? "border-t-2 border-gray-300 mt-1 pt-3" : "border-b border-gray-50"} ${indent ? "pl-4" : ""}`}>
      <span className={`text-xs ${bold || total ? "font-bold text-gray-900" : "text-gray-600"} leading-tight pr-2`}>{label}</span>
      <div className="flex items-center gap-3 flex-shrink-0">
        {count != null && <span className="text-[10px] text-gray-400 hidden md:inline">{count} txns</span>}
        <span className={`text-xs font-bold text-right ${negative ? "text-red-600" : total ? "text-teal-700" : "text-gray-800"}`}>
          {negative ? `(${fmt(amount)})` : fmt(amount)}
        </span>
      </div>
    </div>
  );
}

// ─── Mobile Order Card ────────────────────────────────────────────────────────
function OrderCard({ order, index }) {
  const ch = classifyChannel(order);
  const chLabel = ch === "counter" ? "Counter" : ch === "online" ? "Online" : ch === "bulk" ? "Bulk" : ch === "custom" ? "Custom" : "Other";
  const chColor = ch === "counter" ? "#0F766E" : ch === "online" ? "#3B82F6" : ch === "bulk" ? "#7C3AED" : "#F59E0B";
  return (
    <div className={`px-4 py-3 border-b border-gray-100 last:border-0 ${index % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-800 text-sm">{order.orderNumber}</span>
            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold"
              style={{ background:`${chColor}15`, color:chColor }}>{chLabel}</span>
          </div>
          {order.customer?.name && <p className="text-xs text-gray-500 mt-0.5 truncate">{order.customer.name}</p>}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {order.paymentMethod && <span className="text-[10px] text-gray-400 capitalize">{order.paymentMethod}</span>}
            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize ${
              isCompleted(order.rawStatus) ? "bg-green-100 text-green-700" :
              isCancelled(order.rawStatus) ? "bg-red-100 text-red-600" :
              isRefunded(order.rawStatus)  ? "bg-purple-100 text-purple-700" :
              "bg-yellow-100 text-yellow-700"
            }`}>{order.rawStatus}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-bold text-gray-900 text-sm">{fmt(order.total || 0)}</p>
          {(order.tax > 0 || order.shipping > 0) && (
            <p className="text-[10px] text-gray-400 mt-0.5">
              {order.tax > 0 ? `GST ${fmt(order.tax)}` : ""}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DayEndReport({ orders: allOrders }) {
  const [selectedDate, setSelectedDate] = useState(today());
  const printRef = useRef(null);

  const dayOrders = useMemo(() => {
    return (allOrders || []).filter(o => o.date && o.date.slice(0, 10) === selectedDate);
  }, [allOrders, selectedDate]);

  const channels = useMemo(() => {
    const g = { counter:[], online:[], bulk:[], custom:[], other:[] };
    dayOrders.forEach(o => { const ch = classifyChannel(o); (g[ch] || g.other).push(o); });
    return g;
  }, [dayOrders]);

  const paymentBreakdown = (orders) => {
    const map = {};
    orders.forEach(o => {
      const pm = (o.paymentMethod || o.payment_method || "Other").trim();
      if (!map[pm]) map[pm] = { method:pm, count:0, amount:0 };
      map[pm].count  += 1;
      map[pm].amount += o.total || 0;
    });
    return Object.values(map).sort((a,b) => b.amount - a.amount);
  };

  const totalRevenue  = dayOrders.reduce((s, o) => s + (o.total    || 0), 0);
  const totalTax      = dayOrders.reduce((s, o) => s + (o.tax      || 0), 0);
  const totalShipping = dayOrders.reduce((s, o) => s + (o.shipping || 0), 0);
  const totalSubtotal = dayOrders.reduce((s, o) => s + (o.subtotal || 0), 0);

  const completedOrders = dayOrders.filter(o => isCompleted(o.rawStatus));
  const cancelledOrders = dayOrders.filter(o => isCancelled(o.rawStatus));
  const refundedOrders  = dayOrders.filter(o => isRefunded(o.rawStatus));
  const pendingOrders   = dayOrders.filter(o => isPending(o.rawStatus) && !isRefunded(o.rawStatus) && !isCancelled(o.rawStatus));
  const paidOrders      = dayOrders.filter(o => (o.payment_status||"").toLowerCase() === "paid");

  const totalCollected  = paidOrders.reduce((s, o) => s + (o.total || 0), 0);
  const cancelledAmount = cancelledOrders.reduce((s, o) => s + (o.total || 0), 0);
  const refundedAmount  = refundedOrders.reduce((s, o) => s + (o.total || 0), 0);
  const pendingAmount   = pendingOrders.reduce((s, o) => s + (o.total || 0), 0);

  const allPayments = paymentBreakdown(dayOrders);
  const cashOrders  = dayOrders.filter(o => COUNTER_METHODS.some(m => (o.paymentMethod||"").toLowerCase().includes(m)));
  const cashTotal   = cashOrders.reduce((s, o) => s + (o.total || 0), 0);
  const cashRefund  = refundedOrders
    .filter(o => COUNTER_METHODS.some(m => (o.paymentMethod||"").toLowerCase().includes(m)))
    .reduce((s, o) => s + (o.total || 0), 0);

  const channelChartData = [
    { name:"Counter", value: channels.counter.reduce((s,o)=>s+(o.total||0),0) },
    { name:"Online",  value: channels.online.reduce((s,o)=>s+(o.total||0),0)  },
    { name:"Bulk",    value: channels.bulk.reduce((s,o)=>s+(o.total||0),0)    },
    { name:"Custom",  value: channels.custom.reduce((s,o)=>s+(o.total||0),0)  },
    { name:"Other",   value: channels.other.reduce((s,o)=>s+(o.total||0),0)   },
  ].filter(d => d.value > 0);

  const statusChartData = [
    { name:"Completed", value:completedOrders.length, color:"#10B981" },
    { name:"Pending",   value:pendingOrders.length,   color:"#F59E0B" },
    { name:"Cancelled", value:cancelledOrders.length, color:"#EF4444" },
    { name:"Refunded",  value:refundedOrders.length,  color:"#7C3AED" },
  ].filter(d => d.value > 0);

  const AXIS = { fontSize:10, fill:"#9CA3AF" };
  const dateLabel = new Date(selectedDate).toLocaleDateString("en-IN", {
    weekday:"long", day:"numeric", month:"long", year:"numeric"
  });

  // Use table only on large screens, cards below
  const useTable = typeof window !== "undefined" && window.innerWidth >= 1024;

  return (
    <>
      <style>{PRINT_CSS}</style>

      <div id="day-end-report" ref={printRef} className="space-y-4 w-full min-w-0">

        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-teal-700 to-teal-800 rounded-2xl p-4 text-white shadow-lg">
          {/* Title row */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <h2 className="text-base font-extrabold tracking-tight leading-tight">Day End Report</h2>
              <p className="text-teal-200 text-xs mt-0.5 leading-snug">{dateLabel}</p>
            </div>
            {/* Controls — stacked on very small, row on bigger */}
            <div className="flex items-center gap-2 no-print flex-shrink-0">
              <label className="flex items-center gap-1.5 bg-white/15 rounded-xl px-2.5 py-1.5 cursor-pointer">
                {/* <Calendar className="w-3.5 h-3.5 text-teal-200 flex-shrink-0" /> */}
                <input
                  type="date"
                  value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)}
                  className="bg-transparent text-white text-xs font-semibold outline-none w-[110px]"
                />
              </label>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors whitespace-nowrap">
                <Printer className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Print</span>
              </button>
            </div>
          </div>

          {/* Quick totals — ALWAYS 2x2 grid, never overflow */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label:"Total Orders",       val: dayOrders.length,     icon: Package },
              { label:"Gross Revenue",      val: fmtK(totalRevenue),   icon: IndianRupee },
              { label:"Collected",          val: fmtK(totalCollected), icon: CheckCircle2 },
              { label:"Pending Collection", val: fmtK(pendingAmount),  icon: Clock3 },
            ].map(({ label, val, icon: Icon }) => (
              <div key={label} className="bg-white/10 rounded-xl px-3 py-2.5">
                <Icon size={16} className="text-white opacity-75 mb-1" />
                <p className="text-base font-extrabold leading-tight">{val}</p>
                <p className="text-teal-200 text-[10px] font-semibold uppercase tracking-wider leading-tight mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── KPI grid — 2 col always, 4 col on xl ── */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <KpiBox label="Total Orders"   value={dayOrders.length}       icon={Package}     color="#0F766E" bg="#F0FDFA" />
          <KpiBox label="Completed"      value={completedOrders.length} icon={CheckCircle} color="#15803D" bg="#DCFCE7"
            sub={`${dayOrders.length ? Math.round(completedOrders.length/dayOrders.length*100) : 0}% rate`} />
          <KpiBox label="Cancelled"      value={cancelledOrders.length} icon={XCircle}     color="#991B1B" bg="#FEE2E2"
            sub={`${fmtK(cancelledAmount)} voided`} />
          <KpiBox label="Pending"        value={pendingOrders.length}   icon={Clock}       color="#854D0E" bg="#FEF9C3"
            sub={`${fmtK(pendingAmount)} outstanding`} />
        </div>

        {/* ── Channel panels — 1 col → 2 col md → 3 col xl ── */}
        <div>
          <h3 className="text-xs font-bold text-gray-700 mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-teal-600 flex-shrink-0" />
            Sales by Channel
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            <ChannelPanel title="Counter / Walk-in" icon={Store}   color="#0F766E" bg="#F0FDFA"
              orders={channels.counter} paymentBreakdown={paymentBreakdown(channels.counter)} />
            <ChannelPanel title="Online Orders"     icon={Globe}   color="#3B82F6" bg="#EFF6FF"
              orders={channels.online}  paymentBreakdown={paymentBreakdown(channels.online)} />
            <div className="space-y-3 md:col-span-2 xl:col-span-1">
              <ChannelPanel title="Bulk Orders" icon={Package} color="#7C3AED" bg="#F5F3FF"
                orders={channels.bulk}  paymentBreakdown={paymentBreakdown(channels.bulk)} />
              {(channels.custom.length > 0 || channels.other.length > 0) && (
                <ChannelPanel title="Custom / Other" icon={FileText} color="#F59E0B" bg="#FFFBEB"
                  orders={[...channels.custom, ...channels.other]}
                  paymentBreakdown={paymentBreakdown([...channels.custom, ...channels.other])} />
              )}
            </div>
          </div>
        </div>

        {/* ── Charts — stack always, side-by-side on xl ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100">
              <div className="w-7 h-7 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-4 h-4 text-teal-600" />
              </div>
              <span className="text-xs font-bold text-gray-800">Revenue by Channel</span>
            </div>
            <div className="p-4">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={channelChartData} margin={{ top:4, right:4, bottom:0, left:0 }}>
                  <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS} tickFormatter={fmtK} axisLine={false} tickLine={false} width={44} />
                  <Tooltip formatter={(v) => fmtK(v)} />
                  <Bar dataKey="value" radius={[5,5,0,0]} name="Revenue">
                    {channelChartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100">
              <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-4 h-4 text-orange-500" />
              </div>
              <span className="text-xs font-bold text-gray-800">Order Status</span>
            </div>
            <div className="p-4">
              {statusChartData.length === 0 ? (
                <div className="h-[160px] flex items-center justify-center text-gray-400 text-sm">No orders</div>
              ) : (
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={statusChartData} cx="50%" cy="45%" innerRadius={42} outerRadius={65}
                      dataKey="value" paddingAngle={3} strokeWidth={0}>
                      {statusChartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip />
                    <Legend iconSize={8} wrapperStyle={{ fontSize:10 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* ── Payment Reconciliation ── */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100">
            <div className="w-7 h-7 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-4 h-4 text-teal-600" />
            </div>
            <span className="text-xs font-bold text-gray-800">Payment Reconciliation</span>
          </div>
          <div className="p-4">
            {/* Stack on mobile/tablet, 2-col on xl */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Collected by Method</p>
                {allPayments.length === 0 ? (
                  <p className="text-sm text-gray-400">No payments for this date.</p>
                ) : (
                  <>
                    {allPayments.map(pm => (
                      <ReconciliationRow key={pm.method} label={pm.method} amount={pm.amount} count={pm.count} />
                    ))}
                    <ReconciliationRow label="TOTAL COLLECTED" amount={totalCollected} bold total />
                  </>
                )}
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Day Summary</p>
                <ReconciliationRow label="Gross Revenue"     amount={totalRevenue}   bold />
                <ReconciliationRow label="  Subtotal"        amount={totalSubtotal}  indent />
                <ReconciliationRow label="  Shipping"        amount={totalShipping}  indent />
                <ReconciliationRow label="  Tax / GST"       amount={totalTax}       indent />
                <ReconciliationRow label="Less: Cancelled"   amount={cancelledAmount} negative />
                <ReconciliationRow label="Less: Refunded"    amount={refundedAmount}  negative />
                <ReconciliationRow label="NET REVENUE"
                  amount={totalRevenue - cancelledAmount - refundedAmount} bold total />
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Collection Status</p>
                  <ReconciliationRow label="Collected (Paid)"   amount={totalCollected} count={paidOrders.length} />
                  <ReconciliationRow label="Pending Collection" amount={pendingAmount}  count={pendingOrders.length} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Cash Register ── */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100">
            <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
              <Banknote className="w-4 h-4 text-green-600" />
            </div>
            <span className="text-xs font-bold text-gray-800">Cash Register Summary</span>
          </div>
          <div className="p-4 space-y-4">
            {/* Always 1-col, 3-col on md+ */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { label:"Cash Sales Today",  val:fmtK(cashTotal),              sub:`${cashOrders.length} transactions`, bg:"bg-green-50",  border:"border-green-100",  tv:"text-green-700",  lv:"text-green-600"  },
                { label:"Cash Refunds",       val:`(${fmtK(cashRefund)})`,      sub:"Returns & cancellations",           bg:"bg-red-50",    border:"border-red-100",    tv:"text-red-600",    lv:"text-red-600"    },
                { label:"Net Cash in Drawer", val:fmtK(cashTotal - cashRefund), sub:"Expected in register",              bg:"bg-teal-50",   border:"border-teal-100",   tv:"text-teal-700",   lv:"text-teal-700"   },
              ].map(c => (
                <div key={c.label} className={`${c.bg} border ${c.border} rounded-xl px-4 py-3 text-center`}>
                  <p className={`text-[10px] ${c.lv} font-semibold uppercase tracking-wider mb-1`}>{c.label}</p>
                  <p className={`text-xl font-extrabold ${c.tv}`}>{c.val}</p>
                  <p className={`text-[10px] ${c.lv} opacity-80 mt-1`}>{c.sub}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-3">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Digital Payments Received</p>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
                {allPayments
                  .filter(pm => !COUNTER_METHODS.some(m => pm.method.toLowerCase().includes(m)))
                  .map(pm => (
                    <div key={pm.method} className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5">
                      <p className="text-xs text-gray-500 capitalize truncate">{pm.method}</p>
                      <p className="text-sm font-bold text-gray-800 mt-0.5">{fmtK(pm.amount)}</p>
                      <p className="text-[10px] text-gray-400">{pm.count} txns</p>
                    </div>
                  ))}
                {allPayments.filter(pm => !COUNTER_METHODS.some(m => pm.method.toLowerCase().includes(m))).length === 0 && (
                  <p className="text-xs text-gray-400 col-span-2">No digital payments for this date.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Orders — Card view on <lg, Table on lg+ ── */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-gray-500" />
              </div>
              <span className="text-xs font-bold text-gray-800">All Orders — {selectedDate}</span>
            </div>
            <span className="text-xs text-gray-400">{dayOrders.length} orders</span>
          </div>

          {dayOrders.length === 0 ? (
            <div className="py-14 text-center">
              <Package className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400">No orders found for {selectedDate}</p>
            </div>
          ) : (
            <>
              {/* Card list — visible below lg */}
              <div className="lg:hidden">
                {dayOrders.map((o, i) => <OrderCard key={o.id} order={o} index={i} />)}
                <div className="px-4 py-3 bg-teal-50 border-t-2 border-teal-200 flex items-center justify-between">
                  <span className="text-xs font-bold text-teal-800">TOTALS ({dayOrders.length} orders)</span>
                  <span className="text-sm font-bold text-teal-800">{fmt(totalRevenue)}</span>
                </div>
              </div>

              {/* Full table — visible on lg+ */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-teal-700 text-white">
                      {["#","Order ID","Customer","Channel","Payment","Status","Subtotal","Tax","Ship","Total"].map((h, i) => (
                        <th key={h} className={`px-3 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${i >= 6 ? "text-right" : "text-left"}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {dayOrders.map((o, i) => {
                      const ch = classifyChannel(o);
                      const chLabel = ch === "counter" ? "Counter" : ch === "online" ? "Online" : ch === "bulk" ? "Bulk" : ch === "custom" ? "Custom" : "Other";
                      const chColor = ch === "counter" ? "#0F766E" : ch === "online" ? "#3B82F6" : ch === "bulk" ? "#7C3AED" : "#F59E0B";
                      return (
                        <tr key={o.id} className={`hover:bg-teal-50/20 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}>
                          <td className="px-3 py-2.5 text-xs text-gray-400">{i+1}</td>
                          <td className="px-3 py-2.5 font-semibold text-gray-800 whitespace-nowrap">{o.orderNumber}</td>
                          <td className="px-3 py-2.5 text-gray-600 max-w-[110px] truncate">{o.customer?.name || "—"}</td>
                          <td className="px-3 py-2.5">
                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                              style={{ background:`${chColor}15`, color:chColor }}>{chLabel}</span>
                          </td>
                          <td className="px-3 py-2.5 text-gray-600 capitalize whitespace-nowrap">{o.paymentMethod || "—"}</td>
                          <td className="px-3 py-2.5">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize whitespace-nowrap ${
                              isCompleted(o.rawStatus) ? "bg-green-100 text-green-700" :
                              isCancelled(o.rawStatus) ? "bg-red-100 text-red-600" :
                              isRefunded(o.rawStatus)  ? "bg-purple-100 text-purple-700" :
                              "bg-yellow-100 text-yellow-700"
                            }`}>{o.rawStatus}</span>
                          </td>
                          <td className="px-3 py-2.5 text-right text-gray-700 whitespace-nowrap">{fmt(o.subtotal || 0)}</td>
                          <td className="px-3 py-2.5 text-right text-gray-700 whitespace-nowrap">{fmt(o.tax || 0)}</td>
                          <td className="px-3 py-2.5 text-right text-gray-700 whitespace-nowrap">{fmt(o.shipping || 0)}</td>
                          <td className="px-3 py-2.5 text-right font-bold text-gray-900 whitespace-nowrap">{fmt(o.total || 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-teal-50 border-t-2 border-teal-200">
                      <td colSpan={6} className="px-3 py-3 text-sm font-bold text-teal-800">TOTALS</td>
                      <td className="px-3 py-3 text-right text-sm font-bold text-teal-800 whitespace-nowrap">{fmt(totalSubtotal)}</td>
                      <td className="px-3 py-3 text-right text-sm font-bold text-teal-800 whitespace-nowrap">{fmt(totalTax)}</td>
                      <td className="px-3 py-3 text-right text-sm font-bold text-teal-800 whitespace-nowrap">{fmt(totalShipping)}</td>
                      <td className="px-3 py-3 text-right text-sm font-bold text-teal-800 whitespace-nowrap">{fmt(totalRevenue)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>

        {/* ── Signature block ── */}
        <div className="bg-gray-50 border border-gray-200 rounded-2xl px-4 py-4">
          <p className="text-[10px] text-gray-400 mb-4 text-center">
            Day End Report · {new Date().toLocaleString("en-IN")} · ManaPrint Admin
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {["Prepared By","Verified By","Manager Sign-off"].map(label => (
              <div key={label} className="text-center">
                <div className="h-10 border-b-2 border-dashed border-gray-300 mb-2" />
                <p className="text-[10px] font-semibold text-gray-400">{label}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </>
  );
}